import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Sparkles, User, ImageIcon, Plus, ImagePlus, Loader2, X } from 'lucide-react';
import AppleHeader from '../../components/AppleHeader';
import { loadLastCrearAnuncioState, type CrearAnuncioModeloState } from '../../utils/crearAnuncioStorage';
import { supabase } from '../../lib/supabase';

type ModeloMode = 'ia' | 'guardados' | 'personalizado';

const PersonalizarModeloEstudioVirtual: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'personalizado';
  const estudioVirtual = true;

  const last = useMemo(() => loadLastCrearAnuncioState(), []);
  const lastModelo = (last?.modelo || null) as CrearAnuncioModeloState | null;

  const FASHION_LABEL = 'Moda e Indumentaria';
  const rubroActual = ((last?.rubro as string | undefined) ?? FASHION_LABEL);
  const isFashion = rubroActual === FASHION_LABEL;

  const modeloModeParam = params.get('modelo_mode') as ModeloMode | null;

  const initialMode: ModeloMode = modeloModeParam || lastModelo?.mode || 'ia';

  const [mode, setMode] = useState<ModeloMode>(initialMode);
  const [guardadoId, setGuardadoId] = useState<string>(
    params.get('modelo_guardado_id') || lastModelo?.guardadoId || '',
  );
  const [guardadoNombre, setGuardadoNombre] = useState<string>(
    params.get('modelo_guardado_nombre') || lastModelo?.guardadoNombre || '',
  );
  const [genero, setGenero] = useState<string>(
    params.get('genero') || lastModelo?.genero || '',
  );
  const [edad, setEdad] = useState<string>(
    params.get('edad') || lastModelo?.edad || '',
  );
  const [tipoCuerpo, setTipoCuerpo] = useState<string>(
    params.get('tipo_cuerpo') || lastModelo?.tipoCuerpo || '',
  );
  const [tonoPiel, setTonoPiel] = useState<string>(
    params.get('tono_piel') || lastModelo?.tonoPiel || '',
  );
  const [colorPelo, setColorPelo] = useState<string>(
    params.get('color_pelo') || lastModelo?.colorPelo || '',
  );
  const [largoPelo, setLargoPelo] = useState<string>(
    params.get('largo_pelo') || lastModelo?.largoPelo || '',
  );
  const [descModelo, setDescModelo] = useState<string>(
    params.get('desc_modelo') || lastModelo?.descripcion || '',
  );

  type SavedModelItem = { id: number; nombre: string; fotoFileName?: string; signedUrl?: string };

  const [savedModels, setSavedModels] = useState<SavedModelItem[]>([]);
  const [loadingSavedModels, setLoadingSavedModels] = useState(false);
  const [savedModelsError, setSavedModelsError] = useState<string | null>(null);
  const [savedModelsFetched, setSavedModelsFetched] = useState(false);
  const [selectedSavedModelId, setSelectedSavedModelId] = useState<number | null>(() => {
    const raw = params.get('modelo_guardado_id') || lastModelo?.guardadoId || '';
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });

  const skinToneOptions = [
    { label: 'Muy clara', color: '#FFDFC4' },
    { label: 'Clara', color: '#F0C8A0' },
    { label: 'Beige', color: '#DEB887' },
    { label: 'Trigueña', color: '#CD853F' },
    { label: 'Oscura', color: '#8B4513' },
    { label: 'Muy oscura', color: '#3C2414' },
  ];
  const hairColorOptions = [
    { label: 'Negro', color: '#232323' },
    { label: 'Castaño oscuro', color: '#4B2E09' },
    { label: 'Castaño claro', color: '#A0522D' },
    { label: 'Rubio', color: '#F8E49B' },
    { label: 'Colorado', color: '#B55239' },
  ];

  const handleBack = () => {
    navigate(-1);
  };

  const [showAddModelCard, setShowAddModelCard] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadConsentA, setUploadConsentA] = useState(false);
  const [uploadConsentB, setUploadConsentB] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadPreviewUrl = useMemo(() => {
    if (!uploadFile) return '';
    try {
      return URL.createObjectURL(uploadFile);
    } catch {
      return '';
    }
  }, [uploadFile]);

  useEffect(() => {
    return () => {
      if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    };
  }, [uploadPreviewUrl]);

  const resetUploadState = useCallback(() => {
    setUploadName('');
    setUploadFile(null);
    setUploadConsentA(false);
    setUploadConsentB(false);
    setUploadSaving(false);
    setUploadError(null);
  }, []);

  const [companyId, setCompanyId] = useState<string | null>(null);

  const ensureCompanyId = useCallback(async () => {
    if (companyId) return companyId;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No se encontró el usuario');
    const { data: profile } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .single();
    const idc = (profile as any)?.id_company as string | undefined;
    if (!idc) throw new Error('No se encontró la empresa del usuario');
    setCompanyId(idc);
    return idc;
  }, [companyId]);

  const fetchSavedModels = useCallback(async () => {
    try {
      setLoadingSavedModels(true);
      setSavedModelsError(null);
      const compId = await ensureCompanyId();
      const { data, error } = await supabase
        .from('modelos')
        .select('id, nombre, foto_file_name')
        .eq('id_company', compId)
        .order('nombre');
      if (error) throw error;
      const rows = (data as any[]) || [];
      const withSigned: SavedModelItem[] = await Promise.all(
        rows.map(async (m) => {
          let signedUrl: string | undefined;
          const fileName: string | undefined = m?.foto_file_name || undefined;
          if (fileName) {
            try {
              const { data: signed } = await supabase.storage
                .from('modelos')
                .createSignedUrl(`${compId}/full/${fileName}`, 3600);
              if (signed?.signedUrl) signedUrl = signed.signedUrl;
            } catch {
            }
          }
          return {
            id: m.id as number,
            nombre: m.nombre as string,
            fotoFileName: fileName,
            signedUrl,
          } as SavedModelItem;
        }),
      );
      setSavedModels(withSigned);
    } catch (e) {
      console.error(e);
      setSavedModelsError(
        e instanceof Error ? e.message : 'Error al cargar modelos guardados',
      );
    } finally {
      setLoadingSavedModels(false);
      setSavedModelsFetched(true);
    }
  }, [ensureCompanyId]);

  const handleUploadSave = useCallback(async () => {
    try {
      setUploadError(null);
      if (!uploadName.trim()) throw new Error('Ingresá un nombre para el modelo');
      if (!uploadFile) throw new Error('Subí una imagen del rostro');
      if (!uploadConsentA || !uploadConsentB) throw new Error('Debés aceptar ambas confirmaciones');
      setUploadSaving(true);
      const compId = await ensureCompanyId();
      const ext = (uploadFile.name.split('.').pop() || 'jpg').toLowerCase();
      const base = uploadName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const fileName = `${Date.now()}-${base || 'modelo'}.${ext}`;
      const targetPath = `${compId}/full/${fileName}`;
      const { error: upErr } = await supabase.storage
        .from('modelos')
        .upload(targetPath, uploadFile, { cacheControl: '3600', upsert: false, contentType: uploadFile.type || 'image/jpeg' });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase
        .from('modelos')
        .insert({ id_company: compId, nombre: uploadName.trim(), foto_file_name: fileName });
      if (insErr) throw insErr;
      await fetchSavedModels();
      setShowAddModelCard(false);
      resetUploadState();
      setUploadSaving(false);
    } catch (e: any) {
      console.error('Error subiendo modelo:', e);
      setUploadError(e?.message || 'No se pudo guardar el modelo');
      setUploadSaving(false);
    }
  }, [uploadName, uploadFile, uploadConsentA, uploadConsentB, ensureCompanyId, fetchSavedModels, resetUploadState]);

  const canContinue = useMemo(() => {
    if (mode === 'ia') return true;
    if (mode === 'guardados') {
      return selectedSavedModelId !== null;
    }
    return Boolean(
      genero.trim() ||
        edad.trim() ||
        tipoCuerpo.trim() ||
        tonoPiel.trim() ||
        colorPelo.trim() ||
        largoPelo.trim() ||
        descModelo.trim(),
    );
  }, [mode, selectedSavedModelId, genero, edad, tipoCuerpo, tonoPiel, colorPelo, largoPelo, descModelo]);

  useEffect(() => {
    if (mode === 'guardados' && !savedModelsFetched && !loadingSavedModels) {
      fetchSavedModels();
    }
  }, [mode, savedModelsFetched, loadingSavedModels, fetchSavedModels]);

  const handleContinue = () => {
    if (!canContinue) return;
    const qp = new URLSearchParams(location.search);

    qp.set('modelo_mode', mode);

    qp.delete('modelo_guardado_id');
    qp.delete('modelo_guardado_nombre');
    qp.delete('genero');
    qp.delete('edad');
    qp.delete('tipo_cuerpo');
    qp.delete('tono_piel');
    qp.delete('color_pelo');
    qp.delete('largo_pelo');
    qp.delete('desc_modelo');

    if (mode === 'guardados') {
      const idTrim = guardadoId.trim();
      const nombreTrim = guardadoNombre.trim();
      if (idTrim) qp.set('modelo_guardado_id', idTrim);
      if (nombreTrim) qp.set('modelo_guardado_nombre', nombreTrim);
    } else if (mode === 'personalizado') {
      const generoTrim = genero.trim();
      const edadTrim = edad.trim();
      const tipoCuerpoTrim = tipoCuerpo.trim();
      const tonoPielTrim = tonoPiel.trim();
      const colorPeloTrim = colorPelo.trim();
      const largoPeloTrim = largoPelo.trim();
      const descTrim = descModelo.trim();

      if (generoTrim) qp.set('genero', generoTrim);
      if (edadTrim) qp.set('edad', edadTrim);
      if (tipoCuerpoTrim) qp.set('tipo_cuerpo', tipoCuerpoTrim);
      if (tonoPielTrim) qp.set('tono_piel', tonoPielTrim);
      if (colorPeloTrim) qp.set('color_pelo', colorPeloTrim);
      if (largoPeloTrim) qp.set('largo_pelo', largoPeloTrim);
      if (descTrim) qp.set('desc_modelo', descTrim);
    }

    const query = qp.toString();
    // Si no es moda, volver a diseña tu imagen directamente (sin pasar por pose)
    if (!isFashion) {
      navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?${query}`);
    } else {
      navigate(`/redes-sociales/crear-anuncio/seleccionar-pose?${query}`);
    }
  };

  const headerTitle = 'Modelo';

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">

        <AppleHeader title={headerTitle} onBack={handleBack} actionLabel="Listo" onAction={handleContinue} actionDisabled={!canContinue} />

        {/* Opciones de modelo */}
        <div className="mt-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Cómo querés definir el modelo</h2>
          <p className="text-sm text-gray-600 mt-1">
            Podés dejar que la IA elija un modelo automático o detallar un modelo guardado/personalizado.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-3">
            {/* Modelo automático */}
            <button
              type="button"
              onClick={() => setMode('ia')}
              className={`w-full text-left border rounded-2xl p-4 flex items-start gap-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                mode === 'ia' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="shrink-0 bg-purple-50 p-2 rounded-md">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Modelo automático</h3>
                  {mode === 'ia' && (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  La IA elige automáticamente un modelo apropiado para tus productos.
                </p>
              </div>
            </button>

            {/* Modelo guardado */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setMode('guardados')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMode('guardados');
                }
              }}
              className={`w-full text-left border rounded-2xl p-4 flex flex-col gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                mode === 'guardados' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 bg-blue-50 p-2 rounded-md">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Modelo guardado</h3>
                    {mode === 'guardados' && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Usá un modelo que ya tengas guardado en tu marca.
                  </p>
                </div>
              </div>

              {mode === 'guardados' && (
                <div className="mt-2">
                  {loadingSavedModels ? (
                    <div className="text-sm text-gray-600">Cargando modelos...</div>
                  ) : savedModelsError ? (
                    <div className="text-sm text-red-600">{savedModelsError}</div>
                  ) : savedModels.length === 0 ? (
                    <div>
                      <div className="text-sm text-gray-600">
                        No tenés modelos guardados todavía.
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAddModelCard(true);
                          }}
                          className="relative text-left bg-white border-0 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group"
                        >
                          <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
                            <div className="w-full aspect-square bg-white flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-500 text-white flex items-center justify-center transition-colors group-hover:bg-blue-600">
                                <Plus className="w-8 h-8 sm:w-9 h-9" />
                              </div>
                            </div>
                          </div>
                          <div className="p-3">
                            <p className="text-sm text-gray-900 truncate font-medium py-1">Añadir modelo</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3">
                      {/* Botón añadir modelo */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddModelCard(true);
                        }}
                        className="relative w-full text-left border border-gray-200 rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors group"
                      >
                        <div className="relative overflow-hidden rounded-xl">
                          <div className="w-full h-40 bg-gray-50 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-blue-500 text-white flex items-center justify-center transition-colors group-hover:bg-blue-600">
                              <Plus className="w-8 h-8" />
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-900 mt-2 font-semibold">
                          Añadir modelo
                        </p>
                      </button>
                      {savedModels.map((m) => {
                        const selected = selectedSavedModelId === m.id;
                        const imgSrc = m.signedUrl || 'https://via.placeholder.com/300x300?text=Modelo';
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const isSelected = selectedSavedModelId === m.id;
                              if (isSelected) {
                                setSelectedSavedModelId(null);
                                setGuardadoId('');
                                setGuardadoNombre('');
                              } else {
                                setSelectedSavedModelId(m.id);
                                setGuardadoId(String(m.id));
                                setGuardadoNombre(m.nombre || '');
                              }
                            }}
                            className={`relative w-full text-left border ${
                              selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'
                            } rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                          >
                            <div className="relative overflow-hidden rounded-xl">
                              {m.signedUrl ? (
                                <img
                                  src={imgSrc}
                                  alt={m.nombre || 'Modelo'}
                                  loading="lazy"
                                  className="w-full h-40 object-cover"
                                />
                              ) : (
                                <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                                  <ImageIcon className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                              {selected && (
                                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                                  <Check className="w-4 h-4" />
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-900 mt-2 font-semibold">
                              {m.nombre || `Modelo #${m.id}`}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modelo personalizado */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setMode('personalizado')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMode('personalizado');
                }
              }}
              className={`w-full text-left border rounded-2xl p-4 flex flex-col gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                mode === 'personalizado' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 bg-emerald-50 p-2 rounded-md">
                  <User className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Modelo personalizado</h3>
                    {mode === 'personalizado' && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Definí las características del modelo para que se ajuste al estilo de tu marca.
                  </p>
                </div>
              </div>

              {mode === 'personalizado' && (
                <div className="mt-2 space-y-5">
                  {/* Género */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Género</label>
                    <div className="flex gap-2">
                      {['Hombre', 'Mujer'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGenero(genero === g ? '' : g)}
                          className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            genero === g ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Edad */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Edad</label>
                    <div className="flex flex-wrap gap-2">
                      {['Bebé', 'Niño', 'Joven (25 años)', 'Adulto/a(30 a 40 años)', 'Mayor'].map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => setEdad(edad === e ? '' : e)}
                          className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            edad === e ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tipo de cuerpo */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Tipo de cuerpo</label>
                    <div className="flex flex-wrap gap-2">
                      {['Promedio', 'Talle Grande', 'Talle Extra Grande'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setTipoCuerpo(tipoCuerpo === c ? '' : c)}
                          className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            tipoCuerpo === c ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tono de piel */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Tono de piel</label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setTonoPiel('')}
                        className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                          tonoPiel === '' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Cualquiera
                      </button>
                      {skinToneOptions.map(({ label, color }) => (
                        <button
                          key={label}
                          type="button"
                          title={label}
                          onClick={() => setTonoPiel(tonoPiel === label ? '' : label)}
                          className={`w-9 h-9 rounded-full border ${
                            tonoPiel === label ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-300'
                          } flex items-center justify-center`}
                          style={{ backgroundColor: color }}
                        >
                          {tonoPiel === label && (
                            <span className="block w-3.5 h-3.5 rounded-full bg-white border border-blue-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color de pelo */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Color de pelo</label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setColorPelo('')}
                        className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                          colorPelo === '' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Cualquiera
                      </button>
                      {hairColorOptions.map(({ label, color }) => (
                        <button
                          key={label}
                          type="button"
                          title={label}
                          onClick={() => setColorPelo(colorPelo === label ? '' : label)}
                          className={`w-9 h-9 rounded-full border ${
                            colorPelo === label ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-300'
                          } flex items-center justify-center`}
                          style={{ backgroundColor: color }}
                        >
                          {colorPelo === label && (
                            <span className="block w-3.5 h-3.5 rounded-full bg-white border border-blue-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Largo de pelo */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Largo de pelo</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setLargoPelo('')}
                        className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                          largoPelo === '' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Cualquiera
                      </button>
                      {['Rapado', 'Corto', 'Medio', 'Largo'].map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setLargoPelo(largoPelo === l ? '' : l)}
                          className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            largoPelo === l ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Descripción adicional */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Descripción adicional</label>
                    <textarea
                      rows={3}
                      value={descModelo}
                      onChange={(e) => setDescModelo(e.target.value)}
                      placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados"
                      className="w-full rounded-md border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer acciones desktop para flujo clásico (no se usa en estudio virtual, pero se deja por consistencia) */}
        {!estudioVirtual && (
          <div className="mt-6 hidden sm:flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm text-sm ${
                canContinue ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile spacer & bottom bar para flujo clásico (no se usa en estudio virtual) */}
      {!estudioVirtual && (
        <>
          <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={handleBack}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Volver
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm text-sm ${
                  canContinue ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continuar
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {showAddModelCard && (
        <div className="fixed inset-0 z-[100000] bg-white sm:bg-brand-black/50 sm:backdrop-blur-sm flex flex-col sm:items-center sm:justify-center p-0 sm:p-4">
          <div className="bg-white w-full h-full sm:h-auto sm:max-w-md shadow-sm sm:rounded-2xl overflow-hidden flex flex-col">
            <div className="px-6 pt-5 pb-0 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Añadir modelo</h3>
              <button
                onClick={() => {
                  setShowAddModelCard(false);
                  resetUploadState();
                }}
                className="p-2 text-gray-400 hover:text-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 flex-1 overflow-y-auto flex flex-col">
              {uploadError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-4">{uploadError}</div>
              )}
              <div className="space-y-4 flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del modelo</label>
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="Ej: Juan Pérez"
                    className="w-full h-10 px-4 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Imagen</label>
                  <p className="text-xs text-gray-600 mb-2 sm:hidden">
                    Subí una imagen del rostro de la persona que querés usar como modelo. La imagen debe ser de frente, desde los hombros hacia arriba, con buena iluminación.
                  </p>
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row items-center gap-4">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 bg-white border border-gray-200 rounded-2xl flex items-center justify-center overflow-hidden">
                      {uploadPreviewUrl ? (
                        <img src={uploadPreviewUrl} alt="Vista previa" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-gray-400 text-sm">Sin imagen</div>
                      )}
                    </div>
                    <div className="flex-1 w-full">
                      <input
                        id="upload-model-file-ev"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                      {!uploadPreviewUrl && (
                        <label
                          htmlFor="upload-model-file-ev"
                          className="inline-flex w-full sm:w-auto justify-center items-center px-3 py-3 sm:py-2 shadow-sm text-sm font-normal rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer bg-blue-500 text-white hover:bg-blue-600"
                        >
                          <ImagePlus className="w-4 h-4 mr-2" />
                          Subir imagen
                        </label>
                      )}
                      <ul className="hidden sm:block text-xs text-gray-600 mt-2 list-disc pl-5 space-y-1">
                        <li>Subí una imagen del rostro de la persona que querés usar como modelo.</li>
                        <li>La imagen debe ser de frente, desde los hombros hacia arriba, con buena iluminación.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500"
                      checked={uploadConsentA}
                      onChange={(e) => setUploadConsentA(e.target.checked)}
                    />
                    <span>
                      Confirmo que esta persona accedió a que yo utilice su imagen, y dio su consentimiento para que se generaran imágenes con su rostro.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500"
                      checked={uploadConsentB}
                      onChange={(e) => setUploadConsentB(e.target.checked)}
                    />
                    <span>
                      Acepto toda responsabilidad legal relacionada al uso de la imagen de esta persona para la generación de imágenes en Atlas.
                    </span>
                  </label>
                </div>
              </div>

              {/* Botones - fijos abajo en mobile */}
              <div className="pt-4 mt-auto flex items-center justify-end gap-3 border-t border-gray-100 sm:border-0 sm:pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModelCard(false);
                    resetUploadState();
                  }}
                  className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleUploadSave}
                  disabled={!uploadName.trim() || !uploadFile || !uploadConsentA || !uploadConsentB || uploadSaving}
                  className={`h-10 px-4 rounded-full ${(!uploadName.trim() || !uploadFile || !uploadConsentA || !uploadConsentB || uploadSaving) ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal`}
                >
                  {uploadSaving ? (
                    <span className="inline-flex items-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Guardando...
                    </span>
                  ) : (
                    'Guardar modelo'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalizarModeloEstudioVirtual;
